import { create, type StoreApi, type UseBoundStore } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import isEqual from 'lodash.isequal';
import type { SavedViewsAdapter, Scope } from './types';

// Placeholder shown before any view has loaded. The real system-view name
// arrives asynchronously via the list query and replaces this via setActiveView.
const PLACEHOLDER_VIEW_NAME = '—';

export interface SavedViewsStoreState<P> {
  activeViewId: number | null;
  activeViewName: string;
  isDirty: boolean;
  savedSnapshot: P | null;
  isApplying: boolean;

  setActiveView: (id: number | null, name: string, snapshot: P | null) => void;
  setDirty: (dirty: boolean) => void;
  setSavedSnapshot: (payload: P) => void;
  reset: () => void;
}

export interface SavedViewsController<P> {
  readonly adapter: SavedViewsAdapter<P>;
  readonly useStore: UseBoundStore<StoreApi<SavedViewsStoreState<P>>>;
  /**
   * Apply a payload to the domain stores and atomically commit the active
   * view + snapshot, suppressing dirty checks during the transient mid-apply
   * states so subscribers don't briefly see `isDirty=true`.
   *
   * Pass `payload === null` to skip the apply step (used by the initial
   * bootstrap path so a system view with `{}` payload doesn't stomp the
   * user's localStorage-persisted state). Any non-null value — including
   * an empty `{}` — is passed through to `adapter.apply`, which should
   * treat missing fields as "reset to defaults".
   */
  loadView: (id: number | null, name: string, payload: P | Record<string, never> | null) => void;
  /** Returns whether the current domain state differs from `savedSnapshot`. */
  computeIsDirty: () => boolean;
  /** Captures the current domain state. Convenience wrapper around `adapter.capture()`. */
  capture: () => P;
}

// Live controllers, one per scope. Session teardown (auth-store logout) resets
// them through `resetSavedViewsSelections` below WITHOUT importing the scope
// adapters: an adapter import from auth-store closes a cycle (auth-store →
// adapter → ./hooks → saved-views-service → api-client → api-interceptors →
// auth-store), and a load that enters it at ./hooks runs the adapter's
// `createSavedViewsHooks(...)` before ./hooks has finished loading.
const liveControllers = new Set<SavedViewsController<unknown>>();

/**
 * Session teardown: clear the persisted saved-view selection of every scope so
 * the next user on this browser starts on their default/system view instead
 * of inheriting the previous user's pick. Live controllers are reset in
 * memory (the persist middleware writes the null selection back); a scope
 * whose adapter module has not been loaded yet only has its localStorage key,
 * which is removed directly.
 */
export function resetSavedViewsSelections(scopes: readonly Scope[]): void {
  liveControllers.forEach((c) => c.useStore.getState().reset());
  scopes.forEach((scope) => {
    try {
      localStorage.removeItem(`saved-view-controller-${scope}`);
    } catch {
      // Storage unavailable (private mode / disabled) — nothing persisted to clear.
    }
  });
}

/**
 * Build a scope-specific saved-views controller.
 *
 * The returned object contains:
 * - `useStore`: a zustand hook exposing the active view / dirty state.
 * - `loadView`: atomic "apply + commit snapshot" for selecting a view.
 * - `computeIsDirty`: pure diff of current state vs snapshot.
 *
 * One controller instance per scope (inventory/trades/...). The adapter
 * owns all domain-specific capture/apply logic.
 */
export function createSavedViewsController<P>(
  adapter: SavedViewsAdapter<P>,
): SavedViewsController<P> {
  const useStore = create<SavedViewsStoreState<P>>()(
    devtools(
      persist(
        (set) => ({
          activeViewId: null,
          activeViewName: PLACEHOLDER_VIEW_NAME,
          isDirty: false,
          savedSnapshot: null,
          isApplying: false,

          setActiveView: (id, name, snapshot) =>
            set({ activeViewId: id, activeViewName: name, isDirty: false, savedSnapshot: snapshot }),

          setDirty: (dirty) => set({ isDirty: dirty }),

          setSavedSnapshot: (payload) => set({ savedSnapshot: payload, isDirty: false }),

          reset: () =>
            set({
              activeViewId: null,
              activeViewName: PLACEHOLDER_VIEW_NAME,
              isDirty: false,
              savedSnapshot: null,
            }),
        }),
        {
          name: `saved-view-controller-${adapter.scope}`,
          // Persist only `activeViewId` — enough to tell "same view as before F5"
          // from a fresh load, so we don't stomp user's unsaved column/filter
          // edits via adapter.apply. `activeViewName` is rehydrated from the
          // views list; `savedSnapshot` from `service.getDetail(id)`.
          partialize: (state) => ({ activeViewId: state.activeViewId }),
        },
      ),
      { name: `saved-views-${adapter.scope}`, enabled: import.meta.env.DEV },
    ),
  );

  const equal = adapter.isEqualPayload ?? ((a: P, b: P) => isEqual(a, b));

  const computeIsDirty = (): boolean => {
    const state = useStore.getState();
    if (state.isApplying) return false;
    const snapshot = state.savedSnapshot;
    if (snapshot === null) return false;
    return !equal(adapter.capture(), snapshot);
  };

  const loadView = (
    id: number | null,
    name: string,
    payload: P | Record<string, never> | null,
  ): void => {
    useStore.setState({ isApplying: true });
    try {
      if (payload !== null) {
        adapter.apply(payload);
      }
      const snapshot = adapter.capture();
      useStore.setState({
        activeViewId: id,
        activeViewName: name,
        savedSnapshot: snapshot,
        isDirty: false,
        isApplying: false,
      });
    } catch (err) {
      useStore.setState({ isApplying: false });
      throw err;
    }
  };

  const controller: SavedViewsController<P> = {
    adapter,
    useStore,
    loadView,
    computeIsDirty,
    capture: adapter.capture,
  };
  liveControllers.add(controller as unknown as SavedViewsController<unknown>);
  return controller;
}
